<template>
	<App title="Register Rider">
		<div class="create-layout">

			<!-- Page header -->
			<div class="create-page-header">
				<div class="create-page-header-left">
					<div class="create-page-icon">
						<i class="fa fa-motorcycle"></i>
					</div>
					<div>
						<div class="create-page-title">Register New Rider</div>
						<div class="create-page-subtitle">Complete the form to onboard a new rider</div>
					</div>
				</div>
				<div class="create-steps d-none d-md-flex">
					<div :class="['create-step', step >= 1 ? (step > 1 ? 'completed' : 'active') : '']">
						<div :class="['create-step-dot', step >= 1 ? (step > 1 ? 'completed' : 'active') : '']">
							<i v-if="step > 1" class="fa fa-check"></i>
							<span v-else>1</span>
						</div>
						<span>Details</span>
					</div>
					<div :class="['create-step-line', step > 1 ? 'done' : '']"></div>
					<div :class="['create-step', step >= 2 ? (step > 2 ? 'completed' : 'active') : '']">
						<div :class="['create-step-dot', step >= 2 ? (step > 2 ? 'completed' : 'active') : '']">
							<i v-if="step > 2" class="fa fa-check"></i>
							<span v-else>2</span>
						</div>
						<span>Documents</span>
					</div>
					<div :class="['create-step-line', step > 2 ? 'done' : '']"></div>
					<div :class="['create-step', step >= 3 ? 'active' : '']">
						<div :class="['create-step-dot', step >= 3 ? 'active' : '']">3</div>
						<span>Review</span>
					</div>
				</div>
			</div>

			<!-- Main card -->
			<div class="create-card">

				<!-- Profile strip -->
				<div class="create-profile-strip">
					<div class="create-profile-avatar">
						<img v-if="avatar" :src="avatar" class="w-100 h-100 object-fit-cover" />
						<i v-else class="fa fa-user"></i>
					</div>
					<div>
						<div class="create-profile-name">{{ firstname || 'New' }} {{ lastname || 'Rider' }}</div>
						<div class="create-profile-sub">Rider profile &mdash; Step {{ step }} of 3</div>
					</div>
				</div>

				<Form ref="form" @submit="onSubmit">

					<!-- ── Step 1: Details ─────────────────────────────────────── -->
					<div v-show="step === 1">

						<!-- Personal Information -->
						<div class="create-section">
							<div class="create-section-label">
								<div class="create-section-title">Personal</div>
								<div class="create-section-desc">Basic details about the rider. Name and contact information.</div>
							</div>
							<div class="create-section-fields">
								<div class="row g-3">
									<div class="col-6">
										<InputField id="firstname" v-model="firstname" name="firstname" rules="required" label="First name" placeholder="First name" />
									</div>
									<div class="col-6">
										<InputField id="lastname" v-model="lastname" name="lastname" rules="required" label="Last name" placeholder="Last name" />
									</div>
									<div class="col-6">
										<InputField id="email" v-model="email" name="email" rules="email" label="Email" type="email" placeholder="rider@example.com" />
									</div>
									<div class="col-6">
										<PhoneField id="phone" v-model="phone" name="phone" rules="required|phone" label="Phone number" placeholder="+234 800 000 0000" />
									</div>
								</div>
							</div>
						</div>

						<!-- Location -->
						<div class="create-section">
							<div class="create-section-label">
								<div class="create-section-title">Location</div>
								<div class="create-section-desc">Rider's address and preferred pickup station.</div>
							</div>
							<div class="create-section-fields">
								<div class="row g-3">
									<div class="col-12">
										<InputField id="street" v-model="street" name="street" rules="required" label="Street address" placeholder="e.g 123 Main Street" />
									</div>
									<div class="col-6">
										<SelectField id="country" v-model="country" name="country" rules="required" :options="type_options" label="Country">
											<option value="Nigeria" selected>🇳🇬 Nigeria</option>
										</SelectField>
									</div>
									<div class="col-6">
										<SelectField id="state" v-model="state" name="state" label="State" :options="state_options" />
									</div>
									<div class="col-12">
										<SelectField id="station_pick" v-model="station_pick" name="station_pick" label="Station pick" :options="station_options" :disabled="!isSuperAdmin" />
									</div>
								</div>
							</div>
						</div>

						<!-- Identification & Earnings -->
						<div class="create-section">
							<div class="create-section-label">
								<div class="create-section-title">Identification</div>
								<div class="create-section-desc">BVN and expected monthly earnings for this rider.</div>
							</div>
							<div class="create-section-fields">
								<div class="row g-3">
									<div class="col-12">
										<InputField id="bvn" v-model="bvn" name="bvn" rules="numeric|length:11" label="Bank Verification Number (BVN)" type="text" placeholder="Enter 11-digit BVN" maxlength="11" />
									</div>
									<div class="col-12">
										<MoneyField id="income" v-model="income" name="income" rules="required|numeric|min:0" currency="NGN" locale="en-NG" placeholder="0.00" label="Monthly income (NGN)" />
									</div>
								</div>
							</div>
						</div>

						<!-- Step 1 footer -->
						<div class="create-footer">
							<button type="button" class="btn btn-sm btn-outline-danger" @click="goBack">
								<i class="fa fa-times me-1"></i> Cancel
							</button>
							<div class="create-footer-actions">
								<button type="button" class="btn btn-sm btn-primary" @click="nextStep">
									Next: Documents <i class="fa fa-arrow-right ms-1"></i>
								</button>
							</div>
						</div>
					</div>

					<!-- ── Step 2: Documents ───────────────────────────────────── -->
					<div v-show="step === 2">

						<!-- Identity Documents -->
						<div class="create-section">
							<div class="create-section-label">
								<div class="create-section-title">Documents</div>
								<div class="create-section-desc">Upload required credentials and identification documents.</div>
							</div>
							<div class="create-section-fields">
								<div class="row g-3">

									<!-- Passport Photo -->
									<div class="col-6">
										<div v-if="avatar" class="upload-done">
											<div class="d-flex align-items-center gap-2">
												<i class="fa fa-check-circle text-success"></i>
												<div>
													<div class="upload-done-name">Passport photo</div>
													<div class="upload-done-sub">Uploaded</div>
												</div>
											</div>
											<button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('avatar')"><i class="fa fa-times"></i></button>
										</div>
										<Dropzone v-else id="dropzone" name="avatar" label="Passport photo" v-model="avatar" :url="route('upload.ajax')" param="data" rules="required" class="dropzone-field" />
									</div>

									<!-- Government ID -->
									<div class="col-6">
										<div v-if="identification" class="upload-done">
											<div class="d-flex align-items-center gap-2">
												<i class="fa fa-check-circle text-success"></i>
												<div>
													<div class="upload-done-name">Government ID</div>
													<div class="upload-done-sub">Uploaded</div>
												</div>
											</div>
											<button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('identification')"><i class="fa fa-times"></i></button>
										</div>
										<Dropzone v-else param="data" rules="required" name="identification" v-model="identification" label="Government ID" :url="route('upload.ajax')" class="dropzone-field" />
									</div>

									<!-- Ownership -->
									<div class="col-6">
										<div v-if="ownership" class="upload-done">
											<div class="d-flex align-items-center gap-2">
												<i class="fa fa-check-circle text-success"></i>
												<div>
													<div class="upload-done-name">Proof of ownership</div>
													<div class="upload-done-sub">Uploaded</div>
												</div>
											</div>
											<button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('ownership')"><i class="fa fa-times"></i></button>
										</div>
										<Dropzone v-else param="data" rules="required" name="ownership" v-model="ownership" label="Proof of ownership" :url="route('upload.ajax')" class="dropzone-field" />
									</div>

									<!-- Signature -->
									<div class="col-6">
										<div v-if="signature" class="upload-done">
											<div class="d-flex align-items-center gap-2">
												<i class="fa fa-check-circle text-success"></i>
												<div>
													<div class="upload-done-name">Digital signature</div>
													<div class="upload-done-sub">Uploaded</div>
												</div>
											</div>
											<button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('signature')"><i class="fa fa-times"></i></button>
										</div>
										<Dropzone v-else param="data" rules="required" name="signature" v-model="signature" label="Digital signature" :url="route('upload.ajax')" class="dropzone-field" />
									</div>

								</div>
							</div>
						</div>

						<!-- Legal Documents -->
						<div class="create-section" style="border-bottom: none;">
							<div class="create-section-label">
								<div class="create-section-title">Legal Documents</div>
								<div class="create-section-desc">Upload signed legal agreements and indemnity forms.</div>
							</div>
							<div class="create-section-fields">
								<div class="row g-3">

									<!-- Agreement -->
									<div class="col-6">
										<div v-if="agreement" class="upload-done">
											<div class="d-flex align-items-center gap-2">
												<i class="fa fa-check-circle text-success"></i>
												<div>
													<div class="upload-done-name">Agreement</div>
													<div class="upload-done-sub">Uploaded</div>
												</div>
											</div>
											<button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('agreement')"><i class="fa fa-times"></i></button>
										</div>
										<Dropzone v-else param="data" rules="required" name="agreement" v-model="agreement" label="Agreement" :url="route('upload.ajax')" class="dropzone-field" />
									</div>

									<!-- Indemnity -->
									<div class="col-6">
										<div v-if="indemnity" class="upload-done">
											<div class="d-flex align-items-center gap-2">
												<i class="fa fa-check-circle text-success"></i>
												<div>
													<div class="upload-done-name">Indemnity</div>
													<div class="upload-done-sub">Uploaded</div>
												</div>
											</div>
											<button type="button" class="btn btn-sm p-0 text-danger" @click="reseturl('indemnity')"><i class="fa fa-times"></i></button>
										</div>
										<Dropzone v-else param="data" rules="required" name="indemnity" v-model="indemnity" label="Indemnity" :url="route('upload.ajax')" class="dropzone-field" />
									</div>

								</div>
							</div>
						</div>

						<!-- Step 2 footer -->
						<div class="create-footer">
							<button type="button" class="btn btn-sm btn-outline-secondary" @click="prevStep">
								<i class="fa fa-arrow-left me-1"></i> Back
							</button>
							<div class="create-footer-actions">
								<button type="button" class="btn btn-sm btn-primary" @click="nextStep">
									Next: Review <i class="fa fa-arrow-right ms-1"></i>
								</button>
							</div>
						</div>
					</div>

					<!-- ── Step 3: Review ──────────────────────────────────────── -->
					<div v-show="step === 3">
						<div class="review-grid">

							<!-- Personal -->
							<div class="review-group">
								<div class="review-group-header">
									<i class="fa fa-user me-2 text-muted"></i> Personal
									<button type="button" class="review-edit-btn" @click="step = 1"><i class="fa fa-pencil-alt"></i> Edit</button>
								</div>
								<div class="review-fields">
									<div class="review-field">
										<span class="review-label">Full Name</span>
										<span class="review-value">{{ firstname }} {{ lastname }}</span>
									</div>
									<div class="review-field">
										<span class="review-label">Email</span>
										<span class="review-value">{{ email || '—' }}</span>
									</div>
									<div class="review-field">
										<span class="review-label">Phone</span>
										<span class="review-value">{{ phone }}</span>
									</div>
								</div>
							</div>

							<!-- Location -->
							<div class="review-group">
								<div class="review-group-header">
									<i class="fa fa-map-marker-alt me-2 text-muted"></i> Location
									<button type="button" class="review-edit-btn" @click="step = 1"><i class="fa fa-pencil-alt"></i> Edit</button>
								</div>
								<div class="review-fields">
									<div class="review-field">
										<span class="review-label">Street</span>
										<span class="review-value">{{ street }}</span>
									</div>
									<div class="review-field">
										<span class="review-label">State</span>
										<span class="review-value">{{ state || '—' }}</span>
									</div>
									<div class="review-field">
										<span class="review-label">Country</span>
										<span class="review-value">{{ country }}</span>
									</div>
									<div class="review-field">
										<span class="review-label">Station</span>
										<span class="review-value">{{ station_pick }}</span>
									</div>
								</div>
							</div>

							<!-- Identification -->
							<div class="review-group">
								<div class="review-group-header">
									<i class="fa fa-id-card me-2 text-muted"></i> Identification
									<button type="button" class="review-edit-btn" @click="step = 1"><i class="fa fa-pencil-alt"></i> Edit</button>
								</div>
								<div class="review-fields">
									<div class="review-field">
										<span class="review-label">BVN</span>
										<span class="review-value">{{ bvn || '—' }}</span>
									</div>
									<div class="review-field">
										<span class="review-label">Monthly Income</span>
										<span class="review-value">{{ income ? `₦${Number(income).toLocaleString()}` : '—' }}</span>
									</div>
								</div>
							</div>

							<!-- Documents -->
							<div class="review-group">
								<div class="review-group-header">
									<i class="fa fa-file-alt me-2 text-muted"></i> Documents
									<button type="button" class="review-edit-btn" @click="step = 2"><i class="fa fa-pencil-alt"></i> Edit</button>
								</div>
								<div class="review-fields">
									<div class="review-field">
										<span class="review-label">Passport Photo</span>
										<span :class="['review-value', avatar ? 'text-success' : 'text-danger']">
											<i :class="['fa', avatar ? 'fa-check-circle' : 'fa-times-circle']"></i>
											{{ avatar ? 'Uploaded' : 'Missing' }}
										</span>
									</div>
									<div class="review-field">
										<span class="review-label">Government ID</span>
										<span :class="['review-value', identification ? 'text-success' : 'text-danger']">
											<i :class="['fa', identification ? 'fa-check-circle' : 'fa-times-circle']"></i>
											{{ identification ? 'Uploaded' : 'Missing' }}
										</span>
									</div>
									<div class="review-field">
										<span class="review-label">Proof of Ownership</span>
										<span :class="['review-value', ownership ? 'text-success' : 'text-danger']">
											<i :class="['fa', ownership ? 'fa-check-circle' : 'fa-times-circle']"></i>
											{{ ownership ? 'Uploaded' : 'Missing' }}
										</span>
									</div>
									<div class="review-field">
										<span class="review-label">Digital Signature</span>
										<span :class="['review-value', signature ? 'text-success' : 'text-danger']">
											<i :class="['fa', signature ? 'fa-check-circle' : 'fa-times-circle']"></i>
											{{ signature ? 'Uploaded' : 'Missing' }}
										</span>
									</div>
								</div>
							</div>

							<!-- Legal Documents -->
							<div class="review-group">
								<div class="review-group-header">
									<i class="fa fa-file-contract me-2 text-muted"></i> Legal Documents
									<button type="button" class="review-edit-btn" @click="step = 2"><i class="fa fa-pencil-alt"></i> Edit</button>
								</div>
								<div class="review-fields">
									<div class="review-field">
										<span class="review-label">Agreement</span>
										<span :class="['review-value', agreement ? 'text-success' : 'text-danger']">
											<i :class="['fa', agreement ? 'fa-check-circle' : 'fa-times-circle']"></i>
											{{ agreement ? 'Uploaded' : 'Missing' }}
										</span>
									</div>
									<div class="review-field">
										<span class="review-label">Indemnity</span>
										<span :class="['review-value', indemnity ? 'text-success' : 'text-danger']">
											<i :class="['fa', indemnity ? 'fa-check-circle' : 'fa-times-circle']"></i>
											{{ indemnity ? 'Uploaded' : 'Missing' }}
										</span>
									</div>
								</div>
							</div>

						</div>

						<!-- Step 3 footer -->
						<div class="create-footer">
							<label class="create-terms" for="terms">
								<input type="checkbox" id="terms" name="terms" v-model="terms" class="form-check-input mt-0 flex-shrink-0" />
								<span>I confirm the information is accurate and accept the
									<a href="#" class="text-dark fw-medium text-decoration-underline">Terms &amp; Conditions</a>
								</span>
							</label>
							<div class="create-footer-actions">
								<button type="button" class="btn btn-sm btn-outline-secondary" @click="prevStep">
									<i class="fa fa-arrow-left me-1"></i> Back
								</button>
								<Button id="action" type="submit" :label="label" :loading="loading" :done="done" class="btn btn-sm btn-primary" :disabled="!terms" />
							</div>
						</div>
					</div>

				</Form>
			</div>

		</div>

		<SidebarEffect ref="sidebar">
			<Profile :data="previewData" />
		</SidebarEffect>

		<div class="position-fixed top-0 end-0 p-3" style="z-index: 9999;">
			<NotivueKeyboard v-slot="{ containersTabIndex }">
				<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
					<AppNotification :item="item" />
				</Notivue>
			</NotivueKeyboard>
		</div>
	</App>
</template>

<style scoped lang="scss">
// ─── Layout ───────────────────────────────────────────────────────────────────
.create-layout {
	max-width: 1100px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

// ─── Page header ──────────────────────────────────────────────────────────────
.create-page-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: #fff;
	border-radius: 12px;
	padding: 1.25rem 1.75rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	gap: 1rem;
	flex-wrap: wrap;
}

.create-page-header-left {
	display: flex;
	align-items: center;
	gap: 0.875rem;
}

.create-page-icon {
	flex-shrink: 0;
	width: 42px;
	height: 42px;
	border-radius: 10px;
	background: linear-gradient(135deg, #1e3a2f, #3a7d52);
	color: #fff;
	font-size: 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 3px 8px rgba(30, 58, 47, 0.2);
}

.create-page-title {
	font-size: 0.95rem;
	font-weight: 700;
	color: #111827;
	line-height: 1.2;
}

.create-page-subtitle {
	font-size: 0.78rem;
	color: #6b7280;
	margin-top: 0.1rem;
}

// ─── Steps ────────────────────────────────────────────────────────────────────
.create-steps {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.create-step {
	display: flex;
	align-items: center;
	gap: 0.4rem;
	font-size: 0.78rem;
	color: #9ca3af;
	font-weight: 500;

	&.active {
		color: #111827;
		font-weight: 600;
	}
}

.create-step-dot {
	width: 26px;
	height: 26px;
	border-radius: 50%;
	border: 1.5px solid #d1d5db;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.72rem;
	font-weight: 600;
	color: #9ca3af;

	&.active {
		background: linear-gradient(135deg, #1e3a2f, #3a7d52);
		border-color: transparent;
		color: #fff;
	}

	&.completed {
		background: #dcfce7;
		border-color: #86efac;
		color: #16a34a;
		font-size: 0.65rem;
	}
}

.create-step-line {
	width: 28px;
	height: 1.5px;
	background: #e5e7eb;
	transition: background 0.3s;

	&.done {
		background: #86efac;
	}
}

// ─── Main card ────────────────────────────────────────────────────────────────
.create-card {
	background: #fff;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	overflow: hidden;
}

// ─── Profile strip ────────────────────────────────────────────────────────────
.create-profile-strip {
	display: flex;
	align-items: center;
	gap: 0.875rem;
	padding: 1rem 1.5rem;
	border-bottom: 1px solid #f1f3f5;
	background: #f9fafb;
}

.create-profile-avatar {
	flex-shrink: 0;
	width: 44px;
	height: 44px;
	border-radius: 50%;
	background: #e5e7eb;
	color: #9ca3af;
	font-size: 1.1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	border: 2px solid #fff;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.create-profile-name {
	font-size: 0.9rem;
	font-weight: 700;
	color: #111827;
}

.create-profile-sub {
	font-size: 0.75rem;
	color: #6b7280;
}

.create-close-btn {
	flex-shrink: 0;
	width: 30px;
	height: 30px;
	border-radius: 6px;
	border: 1px solid #e5e7eb;
	background: #fff;
	color: #6b7280;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.75rem;
	cursor: pointer;
	transition: background 0.15s, color 0.15s;

	&:hover {
		background: #fef2f2;
		color: #ef4444;
		border-color: #fecaca;
	}
}

// ─── Sections ─────────────────────────────────────────────────────────────────
.create-section {
	display: grid;
	grid-template-columns: 220px 1fr;
	gap: 0;
	border-bottom: 1px solid #f1f3f5;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
}

.create-section-label {
	padding: 1.5rem;
	border-right: 1px solid #f1f3f5;

	@media (max-width: 640px) {
		border-right: none;
		border-bottom: 1px solid #f1f3f5;
		padding-bottom: 1rem;
	}
}

.create-section-title {
	font-size: 0.85rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.3rem;
}

.create-section-desc {
	font-size: 0.78rem;
	color: #6b7280;
	line-height: 1.55;
}

.create-section-fields {
	padding: 1.5rem;
}

// ─── Uploads ──────────────────────────────────────────────────────────────────
.upload-done {
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-radius: 10px;
	padding: 0.6rem 0.875rem;
	border: 1px solid #e5e7eb;
	background: #f9fafb;
	animation: fadeSlideIn 0.2s ease;
}

.upload-done-name {
	font-size: 0.8rem;
	font-weight: 600;
	color: #111827;
}

.upload-done-sub {
	font-size: 0.7rem;
	color: #6b7280;
}

.dropzone-field {
	border: 1.5px dashed #d1d5db;
	border-radius: 10px;
	background: #fafafa;
	transition: border-color 0.2s, background 0.2s;
	min-height: 80px;

	&:hover {
		border-color: #3a7d52;
		background: #f0fdf4;
	}
}

@keyframes fadeSlideIn {
	from { opacity: 0; transform: translateY(4px); }
	to   { opacity: 1; transform: translateY(0); }
}

// ─── Footer ───────────────────────────────────────────────────────────────────
.create-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	padding: 1rem 1.5rem;
	background: #f9fafb;
	border-top: 1px solid #f1f3f5;
	flex-wrap: wrap;
}

.create-terms {
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	font-size: 0.8rem;
	color: #555;
	cursor: pointer;
	margin: 0;
}

.create-footer-actions {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-shrink: 0;
}

// ─── Review ───────────────────────────────────────────────────────────────────
.review-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 1px;
	background: #f1f3f5;
	border-top: 1px solid #f1f3f5;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
}

.review-group {
	background: #fff;
	padding: 1.25rem 1.5rem;
}

.review-group-header {
	display: flex;
	align-items: center;
	font-size: 0.8rem;
	font-weight: 700;
	color: #374151;
	margin-bottom: 0.875rem;
	padding-bottom: 0.625rem;
	border-bottom: 1px solid #f1f3f5;
}

.review-edit-btn {
	margin-left: auto;
	background: none;
	border: none;
	font-size: 0.72rem;
	color: #16a34a;
	font-weight: 600;
	cursor: pointer;
	padding: 0;
	display: flex;
	align-items: center;
	gap: 0.25rem;

	&:hover { text-decoration: underline; }
}

.review-fields {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.review-field {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 0.5rem;
	font-size: 0.8rem;
}

.review-label {
	color: #9ca3af;
	font-weight: 500;
	flex-shrink: 0;
}

.review-value {
	color: #111827;
	font-weight: 600;
	text-align: right;
	word-break: break-word;
}
</style>

<script lang="ts">
import $ from 'jquery'
import locations from '@/json/location.json'
import { useConfirm } from '@/libs/useConfirm'
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import DropzoneField from '@/vue/components/DropzoneField.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import MoneyField from '@/vue/components/MoneyField.vue'
import PhoneField from '@/vue/components/PhoneField.vue'
import Profile from '@/vue/components/Profile.vue'
import SelectField from '@/vue/components/SelectField.vue'
import SidebarEffect from '@/vue/components/SidebarEffect.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { Options as Select2Options } from 'select2'
import Swal from 'sweetalert2'
import { Component, Ref, toNative, Vue, Watch } from 'vue-facing-decorator'
import Dropzone from '@/widget/Dropzone.vue'
import AppNotification from '@/vue/components/AppNotification.vue'
import { materialTheme, Notification, NotificationProgress, Notivue, NotivueKeyboard, push } from 'notivue'
import { onMounted } from 'vue'

@Component({
    components: {
        Block, Form, InputField, PhoneField, Button, Alert, App,
        SelectField, DropzoneField, Dropzone, SidebarEffect, Profile,
        MoneyField, Notivue, Notification, NotificationProgress,
        NotivueKeyboard, AppNotification,
    },
})
class Create extends Vue {
    loading = false
    done = false
    message = ''
    step = 1

    firstname = ''
    lastname = ''
    email = ''
    phone = ''
    street = ''
    station_pick = ''
    state = ''
    country = ''
    identification = ''
    ownership = ''
    avatar = ''
    bvn = ''
    income = ''
    signature = ''
    agreement = ''
    indemnity = ''
    originalFileName = ''
    terms = false
    confirm = useConfirm().confirm
    materialTheme = materialTheme

    get type_options(): Select2Options {
        return {
            placeholder: 'Select Country',
            minimumResultsForSearch: Infinity,
            //@ts-ignore
            ajax: null,
        }
    }

    //@Todo: Refix when cluster is introduce
    get station_options(): Select2Options {
		return {
			placeholder: 'Select Station',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('station.ajax.select.station'),
			},
            data: !this.isSuperAdmin ? [{
			id: this.$user?.workplace?.slug,
			text: `${this.$user?.workplace?.title}`,
			title: `${this.$user?.workplace?.title}`,
			selected: true,
			}]:[],
			//@ts-ignore
			templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
		}
	}

    get state_options(): Select2Options {
        const nigeria = locations.data.find((loc) => loc.country === 'Nigeria')
        return {
            ajax: null,
            placeholder: 'Select State',
            minimumResultsForSearch: Infinity,
            data: nigeria
                ? nigeria.states.map((state) => ({ id: state, text: state }))
                : [],
        }
    }

    @Ref('sidebar') readonly sidebar!: any
    @Ref('form') readonly form!: InstanceType<typeof Form>
    @Ref('alert') readonly alert!: InstanceType<typeof Alert>

    @Watch('message')
    onMessageChanged(value: string) {
        if (value.length > 0) { this.alert.show() } else { this.alert.hide() }
    }

    handleFileUpload(response) { this.originalFileName = response.original_name }
    reseturl(field: string) { this[field] = '' }

    get label(): ButtonLabel {
        return {
            normal: 'Register Rider',
            loading: 'Registering...',
            done: 'Rider Registered',
        }
    }

    get isSuperAdmin(){
        return this.$user?.role === "super_admin"
    }

    get userLocation(){
        return this.$user?.workplace
    }

    getFilename(url: string): string { return url?.split('/').pop() || '' }

    get previewData() {
        return {
            firstname: this.firstname, lastname: this.lastname,
            email: this.email, phone: this.phone, street: this.street,
            state: this.state, country: this.country, bvn: this.bvn,
            income: this.income, avatar: this.avatar,
            id_card: this.identification, owner_doc: this.ownership,
            signature: this.signature,
            agreement: this.agreement,
            indemnity: this.indemnity,
        }
    }

	mounted(){
		this.station_pick = this.$user?.workplace?.slug ?? ''
	}

    async nextStep() {
        if (this.step === 1) {
            const { errors } = await this.form.validate()
            const step1Fields = ['firstname', 'lastname', 'phone', 'street', 'country', 'station_pick', 'income']
            if (step1Fields.some((f) => errors[f])) return
        }
        if (this.step === 2) {
            if (!this.avatar || !this.identification || !this.ownership || !this.signature || !this.agreement || !this.indemnity) {
                push.error({ title: 'Incomplete', message: 'Please upload all required documents before continuing.' })
                return
            }
        }
        this.step++
    }

    prevStep() {
        if (this.step > 1) this.step--
    }

    onOpenSidebar() { this.sidebar.show() }

    async confirmAction() {
        const isConfirmed = await this.confirm('Are you sure you want to cancel this operation?', {
            title: 'Cancel Process', confirmText: 'Yes, Cancel', cancelText: 'No, Continue',
        })
        if (!isConfirmed) return
        setTimeout(() => {
            this.loading = false
            this.router.visit(this.route('rider.page.group'))
        }, 300)
    }

    onSubmit(payload) {
        this.loading = true
        payload.avatar = this.avatar
        payload.identification = this.identification
        payload.ownership = this.ownership
        payload.signature = this.signature
        payload.agreement = this.agreement
        payload.indemnity = this.indemnity
        payload.income = Number(this.income) * 100
        this.$http
            .post(this.route('rider.ajax.create'), payload)
            .then((response) => {
                this.done = true
                this.message = response?.data?.message || 'Rider created successfully.'
                Swal.fire({ title: 'Success!', text: this.message, icon: 'success', confirmButtonText: 'OK' })
                    .then(() => {
                        this.router.visit(this.route('rider.page.group'))
                        this.message = ''
                        Swal.close()
                    })
            })
            .catch((e) => {
                this.message = e.response?.data?.message || 'An error occurred.'
                push.error({ title: 'Error', message: this.message })
            })
            .finally(() => (this.loading = false))
    }

	goBack() {
		window.history.back()
	}
}

export default toNative(Create)
</script>